import { LoanType } from '~/enums';
import { formatAmount } from '~/helpers/formatters.helper';
import React, { useMemo } from 'react';
import useTranslate, { useCampaignTypeTranslate } from '~/hooks/useTranslate';
import { useSelector } from 'react-redux';
import { RootState } from '~/store';
import useDateFormatter from '~/hooks/useDateFormatter';
import { Body, H6, Stack } from '@qred/components-library';

import { SvgSrc } from '~/components/shared/Icon/Icon';
import Table, {
  CellData,
} from '~/components/shared/PaymentCalculatorTable/PaymentCalculatorTable';
import Spinner from '~/components/shared/Spinner/SpinnerCircular';
import { useGetPaymentPlanQuery } from '~/store/apis/endpoints/onboardingApi/onboarding.api';

interface PaymentCalculatorModalProps {
  applicationUuid: string;
  amount: number;
  loanTerm: number;
}

const PaymentCalculatorModal: React.FC<PaymentCalculatorModalProps> = ({
  applicationUuid,
  amount,
  loanTerm,
}) => {
  const { form, overview } = useSelector((state: RootState) => state.loanOffer);
  const formatDate = useDateFormatter();
  const translate = useTranslate();

  const { data: paymentPlanData, isFetching, isError } = useGetPaymentPlanQuery(
    {
      applicationUuid,
      loanAmount: amount,
      loanTerm,
    }
  );

  // Track the max loan period
  const maxLoanPeriod = form.loanPeriod;

  // Transform payment plan data into table format
  const [tableData, footers] = useMemo(() => {
    // If we don't have payment plan data, return empty
    if (!paymentPlanData) {
      return [[], []];
    }

    const _tableData: Array<Array<CellData>> = [];
    let totalAmortization = 0;
    let totalMonthlyFee = 0;
    let totalToPay = 0;

    // Transform API response to table data, limiting to maxLoanPeriod
    paymentPlanData.installments
      .slice(0, maxLoanPeriod)
      .forEach((installment) => {
        totalAmortization += installment.amortization;
        totalMonthlyFee += installment.monthlyFee;
        totalToPay += installment.totalAmount;

        const rowData: Array<CellData> = [
          { value: installment.installment },
          { value: formatDate(new Date(installment.dueDate)) },
          { value: formatAmount(installment.amortization) },
          { value: formatAmount(installment.monthlyFee) },
          {
            value: formatAmount(installment.totalAmount),
            isFullWidthCell: true,
          },
        ];

        _tableData.push(rowData);
      });

    const _footers = [
      { value: '', isEmptyCell: true },
      { value: translate('Total') as string, isTotalLabel: true },
      { value: formatAmount(totalAmortization) },
      { value: formatAmount(totalMonthlyFee) },
      { value: formatAmount(totalToPay) },
    ];

    return [_tableData, _footers];
  }, [paymentPlanData, formatDate, translate, maxLoanPeriod]);

  const monthlyFeeLabel =
    overview.loanType === LoanType.ANNUITY_LOAN
      ? translate('Loans.MonthlyInterest')
      : translate('MonthlyFee');

  const headers = [
    { value: translate('Installment') as string, iconSrc: SvgSrc.ChartPie },
    { value: translate('DueDate') as string, iconSrc: SvgSrc.Clock },
    { value: translate('Amortization') as string, iconSrc: SvgSrc.ChartArea },
    { value: monthlyFeeLabel as string, iconSrc: SvgSrc.Coins },
    { value: translate('ToPay') as string, iconSrc: SvgSrc.MoneyBillAlt },
  ];

  const campaignTypeTranslate = useCampaignTypeTranslate(
    paymentPlanData?.campaign?.type || null,
    paymentPlanData?.campaign?.principalFreeMonths,
    paymentPlanData?.campaign?.interestRateDiscount
  );

  // Show error state for initial load failures
  if (isError && !paymentPlanData) {
    return <Body size="lg">Error: Failed to fetch payment plan</Body>;
  }

  // Show full-window loading state
  if (isFetching) {
    return (
      <Stack align="center" justify="center" mih={500} w="100%">
        <Spinner />
      </Stack>
    );
  }

  return (
    <Stack spacing="sm" mt={24}>
      {paymentPlanData?.campaign?.type && (
        <H6>
          {translate('PaymentCalculator.CampaignApplied')} -{' '}
          {campaignTypeTranslate}
        </H6>
      )}
      <Stack spacing="sm" pos="relative" mih={400}>
        {!paymentPlanData ? (
          <Body size="lg">
            {translate('PaymentCalculator.NoPaymentPlanAvailable')}
          </Body>
        ) : (
          <>
            <Table
              center
              data={tableData}
              headers={headers}
              footers={footers}
            />
            <Body size="sm">
              {translate('PaymentCalculator.FooterExplanation')}
            </Body>
            {overview.loanType === LoanType.ANNUITY_LOAN && (
              <Body size="sm">
                {translate('PaymentCalculator.AnnuityLoanDisclaimer')}
              </Body>
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default PaymentCalculatorModal;
